import React, {
  FunctionComponent, useEffect, useRef, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import {
  Classes, Dialog, ProgressBar
} from '@blueprintjs/core';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { JurisdictionArea, JAreaAfRowData } from 'data/jurisdictionArea/jurisdictionArea.types';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/jurisdictionArea/jurisdictionArea.actions';
import { newJurisdictionArea } from 'routes/JurisdictionArea/jurisdictionArea.types';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import { showSuccessMessage, showError, showWarning } from 'shared/toaster';
import { Ref } from 'routes/JurisdictionArea/components/JurisdictionAreaForm/JurisdictionAreaForm';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import ModalDialog from 'shared/modal/dialog';
import useConfirmActionDialog from 'utils/hooks/useConfirmActionDialog';
import AddAfDialogBody from 'routes/JurisdictionArea/components/JurisdictionAreaDialog/AddAccessFunctions';
import JurisdictionAreaDialog from '../../components/JurisdictionAreaDialog';
import JurisdictionAreaForm from '../../components/JurisdictionAreaForm';
import { jurisdictionAreaTemplate } from '../../templates/jurisdictionAreaTemplate';
import { adaptTemplateVisibilityMode } from '../../utils/jurisdictionAreaTemplate.adapters';
import getJurisdictionAreaDataToCreate from '../../utils/getJurisdictionAreaDataToSubmit';
import '../../jurisdiction-area.scss';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  jurisdictionAreaTemplateDef: adaptTemplateVisibilityMode(
    jurisdictionAreaTemplate,
    'create',
  ),
});

const mapDispatch = {
  createJurisdictionArea: actions.createJurisdictionArea,
  goToJurisdictionAreaList: actions.goToJurisdictionAreaList,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const JurisdictionAreaCreate: FunctionComponent<Props> = ({
  deliveryFunctionId,
  history,
  isAuditEnabled,
  userId,
  userName,
  accessFunctions,
  jurisdictionAreaTemplateDef,
  createJurisdictionArea,
  goToJurisdictionAreaList,
  getAccessFunctions
}: Props) => {
  const formRef = useRef<Ref>(null);
  const [template] = useState(jurisdictionAreaTemplateDef);
  const [formData, setFormData] = useState<JurisdictionArea>(cloneDeep(newJurisdictionArea));
  const [gridData, setGridData] = useState<JAreaAfRowData[]>([]);
  const [selectedAccessFunctions, setSelectedAccessFunctions] = useState<JAreaAfRowData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [confirmActionDialog, setConfirmActionDialog] = useConfirmActionDialog();
  const [accessFunctionPending, setAccessFunctionPending] = useState<string>('');
  const [failedAccessFunctions, setFailedAccessFunctions] = useState<string[]>([]);
  const [isJurisdictionAreaDialogOpen, setIsJurisdictionAreaDialogOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    getAccessFunctions(deliveryFunctionId);
  }, [deliveryFunctionId]);

  useEffect(() => {
    const adaptedGridData: JAreaAfRowData[] = accessFunctions.map((accessFunction) => {
      const adaptedGridRow: JAreaAfRowData = {
        id: accessFunction.id || '',
        AFTYPE: accessFunction.type,
        AFNAME: accessFunction.name
      }
      return adaptedGridRow
    })
    setGridData(adaptedGridData);
  }, [accessFunctions]);

  const handleFieldUpdate = ({
    name: fieldName,
    value
  }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value
    });
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    setSelectedAccessFunctions(event.api.getSelectedRows());
  };

  const handleClickSave = () => {
    const isValid =
      formRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = formRef?.current?.getValues();
    if (!formValues) {
      return;
    }

    if (selectedAccessFunctions.length === 0) {
      setConfirmActionDialog(
        t('jurisdictionArea.addAccessFunction'),
        <AddAfDialogBody />,
        t('common.button.ok'),
      );
    }

    if (selectedAccessFunctions.length) {
      let numAccessFunctionsHandled = 0;
      const accessFunctionsFailedToBeCreated: string[] = [];
      setIsLoading(true);
      setAccessFunctionPending(selectedAccessFunctions[0].AFNAME);
      setFailedAccessFunctions([]);
      setProgressBarValue(0);
      let allAccessFunctionsCreated = true;

      selectedAccessFunctions.map(async (selectedAccessFunction, index) => {
        const body = getJurisdictionAreaDataToCreate(
          formValues,
          null,
          isAuditEnabled,
          userId,
          userName,
          selectedAccessFunction
        );

        const res = await createJurisdictionArea(deliveryFunctionId, body);
        if (res === undefined) {
          allAccessFunctionsCreated = false;
          accessFunctionsFailedToBeCreated.push(`${selectedAccessFunction.AFNAME} (${selectedAccessFunction.AFTYPE})`);
        }
        if (index === selectedAccessFunctions.length - 1) {
          setIsLoading(false);
          if (allAccessFunctionsCreated || (accessFunctionsFailedToBeCreated.length < selectedAccessFunctions.length)) {
            if (accessFunctionsFailedToBeCreated.length) {
              setFailedAccessFunctions(accessFunctionsFailedToBeCreated);
              setIsJurisdictionAreaDialogOpen(true);
            } else {
              goToJurisdictionAreaList(history);
              showSuccessMessage(i18n.t('jurisdictionArea.create.success', { areaName: formValues.SS8ID }), 5000);
            }
          } else {
            goToJurisdictionAreaList(history);
            showError(i18n.t('jurisdictionArea.create.failed', { areaName: body.data?.id, error: 'Error' }));
          }
          return;
        }
        numAccessFunctionsHandled += 1;
        setProgressBarValue(numAccessFunctionsHandled / selectedAccessFunctions.length);
        setAccessFunctionPending(selectedAccessFunctions[index + 1].AFNAME);
      });
    }
  };

  const handleClickCancel = () => goToJurisdictionAreaList(history);

  const closeJurisdictionAreaDialog = () => {
    setIsJurisdictionAreaDialogOpen(false);
    goToJurisdictionAreaList(history);
    const jurisdictionAreaName = formRef?.current?.getValues().SS8ID;
    showSuccessMessage(i18n.t('jurisdictionArea.create.success', { areaName: jurisdictionAreaName }), 5000);
  }

  const renderForm = () => {
    if (gridData && template) {
      return (
        <JurisdictionAreaForm
          ref={formRef}
          template={template}
          gridData={gridData}
          onSelectionChanged={handleSelectionChanged}
          onFieldUpdate={handleFieldUpdate}
          mode="create"
        />
      )
    }
  };

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="JurisdictionAreaCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('jurisdictionArea.create.title'))}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
      <Dialog
        title={t('jurisdictionArea.dialog.create.title')}
        isOpen={isLoading}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
      >
        <div className={Classes.DIALOG_BODY}>
          <div className="jurisdiction-area-creating-access-functions">{t('jurisdictionArea.dialog.create.description', { accessFunctionPending })}</div>
          <ProgressBar className=".jurisdiction-area-creating-access-functions-progress" intent="primary" value={progressBarValue} />
        </div>
      </Dialog>
      <JurisdictionAreaDialog
        title={t('jurisdictionArea.dialog.created.title')}
        description={t('jurisdictionArea.dialog.created.description')}
        isJurisdictionAreaDialogOpen={isJurisdictionAreaDialogOpen}
        accessFunctionsList={failedAccessFunctions}
        handleClose={closeJurisdictionAreaDialog}
      />
      <ModalDialog
        width="500px"
        isOpen={confirmActionDialog.isOpen}
        title={confirmActionDialog.title}
        customComponent={confirmActionDialog.customComponent}
        actionText={confirmActionDialog.actionText}
        onSubmit={confirmActionDialog.onSubmit}
        onClose={confirmActionDialog.onClose}
        doNotDisplayCancelButton={confirmActionDialog.hideCancel}
      />
    </div>
  );
};

export default withRouter(connector(JurisdictionAreaCreate));
